import React, { useState } from 'react';
import {
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import type { Theme } from '@mui/material/styles';
import {
  Palette as PaletteIcon,
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon,
  SportsSoccer as SportsSoccerIcon,
  Favorite as FavoriteIcon,
  Flag as FlagIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import { useTheme as useAppTheme } from '../contexts/ThemeContext';
import {
  ALL_THEME_OPTIONS,
  AppThemeId,
} from '../themes/themeRegistry';

const themeIcons: Record<AppThemeId, React.ReactElement> = {
  default: <LightModeIcon fontSize="small" />,
  dark: <DarkModeIcon fontSize="small" />,
  copa: <SportsSoccerIcon fontSize="small" />,
  rosa: <FavoriteIcon fontSize="small" />,
  usa: <FlagIcon fontSize="small" />,
};

interface ThemeMenuButtonProps {
  isDarkMode: boolean;
  isCopa?: boolean;
  size?: 'small' | 'medium' | 'large';
}

const headerActionButtonSx = (isDarkMode: boolean, isCopa = false) => ({
  color: 'inherit',
  borderRadius: 2,
  ...(isDarkMode
    ? {
        '&:hover': {
          bgcolor: alpha('#3b82f6', 0.2),
        },
      }
    : isCopa
      ? {
          '&:hover': {
            bgcolor: alpha('#013f1e', 0.08),
          },
        }
      : {
          '&:hover': {
            bgcolor: alpha('#fff', 0.15),
          },
        }),
});

const headerMenuPaperSx = (theme: Theme, isDarkMode: boolean) => ({
  elevation: 0,
  overflow: 'visible',
  filter: isDarkMode
    ? 'drop-shadow(0px 8px 24px rgba(0,0,0,0.55))'
    : 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
  mt: 1.5,
  minWidth: 240,
  ...(isDarkMode && {
    bgcolor: theme.palette.background.paper,
    border: `1px solid ${alpha('#3b82f6', 0.18)}`,
    backgroundImage: 'none',
  }),
});

export const ThemeMenuButton: React.FC<ThemeMenuButtonProps> = ({
  isDarkMode,
  isCopa = false,
  size = 'medium',
}) => {
  const theme = useTheme();
  const { themeId, setThemeId } = useAppTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = (id: AppThemeId) => {
    setThemeId(id);
    handleClose();
  };

  const renderOption = (option: (typeof ALL_THEME_OPTIONS)[number]) => {
    const selected = themeId === option.id;
    const disabled = !option.available;

    return (
      <MenuItem
        key={option.id}
        disabled={disabled}
        selected={selected}
        onClick={() => option.available && handleSelect(option.id)}
        sx={{ py: 1, alignItems: 'flex-start' }}
      >
        <ListItemIcon sx={{ minWidth: 36, mt: 0.25, color: selected ? 'primary.main' : 'inherit' }}>
          {themeIcons[option.id] ?? <PaletteIcon fontSize="small" />}
        </ListItemIcon>
        <ListItemText
          primary={option.label}
          secondary={disabled ? 'Em breve' : option.description}
          primaryTypographyProps={{ fontWeight: selected ? 700 : 500, fontSize: '0.875rem' }}
          secondaryTypographyProps={{ fontSize: '0.75rem' }}
        />
        {selected && (
          <CheckIcon color="primary" sx={{ fontSize: 18, ml: 1, mt: 0.5 }} />
        )}
      </MenuItem>
    );
  };

  return (
    <>
      <Tooltip title="Temas">
        <IconButton
          color="inherit"
          size={size}
          onClick={handleOpen}
          aria-label="Selecionar tema"
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          sx={headerActionButtonSx(isDarkMode, isCopa)}
        >
          <PaletteIcon />
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          elevation: 0,
          sx: headerMenuPaperSx(theme, isDarkMode),
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem disabled sx={{ opacity: 1, py: 0.75, minHeight: 0 }}>
          <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: 1, color: 'text.secondary' }}>
            Aparência
          </Typography>
        </MenuItem>

        {ALL_THEME_OPTIONS.filter((option) => option.available).map(renderOption)}
      </Menu>
    </>
  );
};
